//******************************************************************************
//キー・座標ユーティリティ

//******************************************************************************
// import
import { latLngToWebMercator, webMercatorToLatLng } from "../Projection/projection";

//******************************************************************************
/**
 * タイル座標からキーを生成する関数
 * @param {{x:number, y:number, z:number}} value タイル座標
 * @return {string} タイル識別子
*/
export const tileCoordsToKey = (value) => {
	const dim = 1 << value.z;
	return [
		(dim * value.y + value.x).toString(36),
		value.z.toString(36),
		value.z.toString(36)
	].join("");
};

//******************************************************************************
/**
 * キーからタイル座標を生成する関数
 * @param {string} value タイル識別子
 * @returns {{x:number, y:number, z:number}} タイル座標
*/
export const keyToTileCoords = (value) => {
	const xyz = {
		z: parseInt(value.slice(-1), 36)
	};
	const xy = parseInt(value.slice(0, value.length - 2), 36);
	const dim = 1 << xyz.z;
	xyz.x = xy % dim;
	xyz.y = (xy - xyz.x) / dim;
	return xyz;
};

//******************************************************************************
/**
 * タイルの四隅の座標（範囲）を決める関数
 * @param {string} tileId タイル識別子
 * @returns {object} bounds
 * @property {{lng: number, lat:number}} bounds._ne 北西端
 * @property {{lng: number, lat:number}} bounds._sw 南東端
*/
const tileToLatLngBounds = (tileId) => {
	const { x, y, z } = keyToTileCoords(tileId);
	const n = Math.pow(2, z);
	const lng1 = (x / n) * 360 - 180;
	const lng2 = ((x + 1) / n) * 360 - 180;
	const lat1 = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))) * 180 / Math.PI;
	const lat2 = Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 1)) / n))) * 180 / Math.PI;
	return {
		_ne: { lng: lng2, lat: lat1 }, // 北東
		_sw: { lng: lng1, lat: lat2 }  // 南西
	};
}

//******************************************************************************
/**
 * 区画を広げる関数
 * @param {object} bounds
 * @property {{lng: number, lat:number}} bounds._ne 北西端
 * @property {{lng: number, lat:number}} bounds._sw 南東端
 * @param {number} [expand]	拡張数（デフォルト1）
 * @returns {object} _bounds
 * @property {{lng: number, lat:number}} _bounds._ne 北西端
 * @property {{lng: number, lat:number}} _bounds._sw 南東端
*/
const expandBounds = (bounds, expand = 1) => {
	const webmercatorBounds = {
		ne: latLngToWebMercator(bounds._ne),
		sw: latLngToWebMercator(bounds._sw),
	};
	const yExpand = (webmercatorBounds.sw.y - webmercatorBounds.ne.y) * (expand - 1) / 2;
	const xExpand = (webmercatorBounds.ne.x - webmercatorBounds.sw.x) * (expand - 1) / 2;
	const webmercatorN = webmercatorBounds.ne.y - yExpand;
	const webmercatorS = webmercatorBounds.sw.y + yExpand;
	const webmercatorW = webmercatorBounds.sw.x - xExpand;
	const webmercatorE = webmercatorBounds.ne.x + xExpand;
	return {
		_ne: webMercatorToLatLng({ x: webmercatorE, y: webmercatorN }),
		_sw: webMercatorToLatLng({ x: webmercatorW, y: webmercatorS }),
	};
};

//******************************************************************************
/**
 * タイルが区画に収まっているか否かを返す関数
 * @param {string} tileId タイル識別子
 * @param {Object} bounds
 * @property {{lng: number, lat:number}} bounds._ne 北西端
 * @property {{lng: number, lat:number}} bounds._sw 南東端
 * @param {number} expand 拡張係数（デフォルト1）
 * @returns {boolean} タイルが区画に収まっているか否か
*/
export const keyInBounds = (tileID, bounds, expand = 1) => {
	const boundsExpanded = typeof expand === "undefined" || expand === 1
		? bounds : expandBounds(bounds, expand);
	const tileBounds = tileToLatLngBounds(tileID);
	return (
		// タイルの北緯がビューポートの南緯以上
		tileBounds._ne.lat >= boundsExpanded._sw.lat
		// タイルの南緯がビューポートの北緯以下
		&& tileBounds._sw.lat <= boundsExpanded._ne.lat
		// タイルの東経がビューポートの西経以上
		&& tileBounds._ne.lng >= boundsExpanded._sw.lng
		// タイルの西経がビューポートの東経以下
		&& tileBounds._sw.lng <= boundsExpanded._ne.lng
	);
};
